import { useState, useMemo, useRef, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import { useAddKeywordToTheme, useGenerateKeywordSuggestions, keywordThemesQueryKey } from "@/resources/keyword-theme";
import type { KeywordSuggestion } from "@/resources/keyword-theme";
import { keywordApi } from "@/resources/keyword";
import { parseKeywordsFromFile } from "@/utils/keywordInput";
import { MIN_KEYWORD_LENGTH } from "@/utils/keywordValidation";
import { searchKeywords, stripQuotes } from "@/utils/keywordSearch";
import type { KeywordTheme, KeywordThemeKeyword } from "@/types/keyword-theme";
import type { Keyword } from "@/types/keyword";
import { SelectedKeywordChip, toggleSetItem } from "./KeywordThemeShared";
import { AddKeywordSignalAiTab } from "./AddKeywordSignalAiTab";
import { AddKeywordPickAddTab } from "./AddKeywordPickAddTab";
import { AddKeywordBulkEntryTab } from "./AddKeywordBulkEntryTab";
import { AddKeywordFileUploadTab } from "./AddKeywordFileUploadTab";
import { toast } from "@/hooks/use-toast";
import queryClient from "@/query-client";

interface AddKeywordToThemeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  theme: KeywordTheme | null;
  productId: number;
  brandId: number | null;
  allKeywords: Keyword[];
}

type Tab = "ai" | "pick" | "bulk" | "upload";

/** Cap applied only after ranking (exact match first, then relevance/volume) — the
 * strongest matches always survive; this just trims the weakest tail of a large
 * result set so the table doesn't render thousands of rows in one pass. */
const PICKABLE_KEYWORDS_LIMIT = 200;

/** A bank keyword the modal knows about, or a just-created one whose only known fields are id/search. */
type StagedKeyword = Partial<Keyword> & Pick<Keyword, "id" | "search">;

export function AddKeywordToThemeModal({
  open,
  onOpenChange,
  theme,
  productId,
  brandId,
  allKeywords,
}: AddKeywordToThemeModalProps) {
  const [tab, setTab] = useState<Tab>("ai");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [aiSuggestions, setAiSuggestions] = useState<KeywordSuggestion[] | null>(null);

  const [bulkText, setBulkText] = useState("");
  const [bulkParsed, setBulkParsed] = useState<string[]>([]);

  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null);
  const [uploadParsed, setUploadParsed] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [typedNewKeywords, setTypedNewKeywords] = useState<string[]>([]);

  const { mutateAsync: addKeyword } = useAddKeywordToTheme();
  const { mutateAsync: generateSuggestions, isPending: isGenerating } = useGenerateKeywordSuggestions();
  const { mutateAsync: addKeywordsToBank, isPending: isResolvingNew } = keywordApi.useAddHeroProductKeywords();

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(t);
  }, [search]);

  const reset = () => {
    setSearch("");
    setDebouncedSearch("");
    setSelectedIds(new Set());
    setTab("ai");
    setAiSuggestions(null);
    setBulkText("");
    setBulkParsed([]);
    setUploadedFileName(null);
    setUploadParsed([]);
    setTypedNewKeywords([]);
  };

  /** New keyword phrases (from Pick/Add typing, Bulk Entry, or File Upload) not yet created in the bank. */
  const pendingNewKeywords = useMemo(
    () => Array.from(new Set([...bulkParsed, ...uploadParsed, ...typedNewKeywords])),
    [bulkParsed, uploadParsed, typedNewKeywords],
  );

  const removePendingNewKeyword = (phrase: string) => {
    setBulkParsed((prev) => {
      const next = prev.filter((k) => k !== phrase);
      setBulkText(next.join("\n"));
      return next;
    });
    setUploadParsed((prev) => prev.filter((k) => k !== phrase));
    setTypedNewKeywords((prev) => prev.filter((k) => k !== phrase));
  };

  const addTypedNewKeyword = (raw: string) => {
    const phrase = raw.trim().toLowerCase();
    if (!phrase) return;
    if (phrase.length < MIN_KEYWORD_LENGTH) {
      toast({
        title: "Keyword too short",
        description: "Keywords must be at least 2 characters.",
        variant: "destructive",
      });
      return;
    }
    setTypedNewKeywords((prev) => (prev.includes(phrase) ? prev : [...prev, phrase]));
    setSearch("");
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadedFileName(file.name);
    try {
      const { keywords, skippedCount } = await parseKeywordsFromFile(file);
      if (keywords.length === 0) {
        toast({
          title: "No keywords found",
          description: "No valid keywords found in the uploaded file. Make sure keywords are listed in Column A.",
          variant: "destructive",
        });
        setUploadParsed([]);
      } else {
        setUploadParsed(keywords);
        if (skippedCount > 0) {
          toast({
            title: "Some keywords skipped",
            description: `${skippedCount} keyword${skippedCount !== 1 ? "s" : ""} skipped — must be at least 2 characters.`,
          });
        }
      }
    } catch {
      toast({
        title: "File parsing failed",
        description: "Failed to parse the uploaded file. Please check the file format and try again.",
        variant: "destructive",
      });
      setUploadParsed([]);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleAskAI = async () => {
    if (!theme) return;
    const result = await generateSuggestions({ productId, themeId: theme.id });
    const suggestions = result.keywords ?? [];
    setAiSuggestions(suggestions);
    // Pre-select all AI suggestions
    setSelectedIds(new Set(suggestions.map((s) => s.keyword_id)));
  };

  const keywordById = useMemo(
    () => new Map(allKeywords.map((k) => [k.id, k])),
    [allKeywords],
  );

  const alreadyInTheme = useMemo(
    () => new Set(theme?.keywords.map((k) => k.keyword_id) ?? []),
    [theme],
  );

  const pickableKeywords = useMemo(() => {
    const base = allKeywords.filter((k) => !alreadyInTheme.has(k.id));
    return searchKeywords(base, debouncedSearch, PICKABLE_KEYWORDS_LIMIT);
  }, [allKeywords, alreadyInTheme, debouncedSearch]);

  const trimmedSearch = stripQuotes(search.trim());
  const exactBankMatch = trimmedSearch
    ? allKeywords.find((k) => k.search.toLowerCase() === trimmedSearch.toLowerCase())
    : undefined;
  const alreadyInThemeMatch = exactBankMatch && alreadyInTheme.has(exactBankMatch.id) ? exactBankMatch : undefined;
  const canAddSearchAsNewKeyword =
    trimmedSearch.length >= MIN_KEYWORD_LENGTH &&
    !exactBankMatch &&
    !pendingNewKeywords.includes(trimmedSearch.toLowerCase());

  const selectedKeywords = useMemo(
    () => allKeywords.filter((k) => selectedIds.has(k.id)),
    [allKeywords, selectedIds],
  );

  const toggleId = (id: number) => setSelectedIds((prev) => toggleSetItem(prev, id));

  const totalSelectedCount = selectedIds.size + pendingNewKeywords.length;

  const handleConfirm = async () => {
    if (!theme || !totalSelectedCount) return;

    const themeId = theme.id;
    const themeName = theme.name;

    // New phrases (Bulk Entry / File Upload) need to exist in the bank before
    // they can be attached to a theme, so resolve them to real IDs first.
    let newlyResolved: StagedKeyword[] = [];
    if (pendingNewKeywords.length) {
      if (!brandId) {
        toast({
          title: "Brand required",
          description: "Select a brand before adding new keywords.",
          variant: "destructive",
        });
        return;
      }
      try {
        const result = await addKeywordsToBank({ productId, brandId, keywords: pendingNewKeywords });
        newlyResolved = result.keywords;
      } catch {
        // addKeywordsToBank's onError (handleApiError) already shows a toast.
        return;
      }
    }

    const kwIds = [...Array.from(selectedIds), ...newlyResolved.map((k) => k.id)];
    const kwsSnap: StagedKeyword[] = [...selectedKeywords, ...newlyResolved];

    // Optimistic update: add keywords to the theme immediately
    const queryKey = keywordThemesQueryKey(productId);
    const previous = queryClient.getQueryData<KeywordTheme[]>(queryKey);
    queryClient.setQueryData<KeywordTheme[]>(queryKey, (old) => {
      if (!old) return old;
      return old.map((t) => {
        if (t.id !== themeId) return t;
        const existingIds = new Set(t.keywords.map((k) => k.keyword_id));
        const newKws: KeywordThemeKeyword[] = kwsSnap
          .filter((kw) => !existingIds.has(kw.id))
          .map((kw) => ({
            id: 0,
            keyword_id: kw.id,
            is_head_term: false,
            search: kw.search,
            search_volume: kw.search_volume ?? null,
            kps: kw.kps ?? null,
            kps_raw: null,
            rank: kw.rank ?? null,
            hero_relevance: kw.hero_relevance ?? null,
            search_volume_trend: kw.search_volume_trend ?? null,
          }));
        return { ...t, keywords: [...t.keywords, ...newKws] };
      });
    });

    // Close immediately
    reset();
    onOpenChange(false);

    try {
      await Promise.all(kwIds.map((id) => addKeyword({ productId, themeId, keywordId: id })));
      // addKeyword.onSuccess invalidates → real data replaces optimistic
      toast({
        title: "Keywords added",
        description: `${kwIds.length} keyword${kwIds.length === 1 ? "" : "s"} added to "${themeName}".`,
      });
    } catch {
      // addKeyword's onError (handleApiError) already shows a toast.
      queryClient.setQueryData(queryKey, previous);
    }
  };

  if (!theme) return null;

  // Bulk Entry has no design yet (spec lists it, design doesn't) — hidden until designed.
  const tabs: { id: Tab; label: string }[] = [
    { id: "ai", label: "✦ Signal AI" },
    { id: "pick", label: "Pick / Add" },
    { id: "upload", label: "File Upload" },
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) reset();
        onOpenChange(v);
      }}
    >
      <DialogContent className="sm:max-w-[680px] p-0 gap-0 flex flex-col h-[620px] overflow-hidden">
        <DialogHeader className="px-6 pt-5 pb-0 shrink-0">
          <DialogTitle>Add keywords to &ldquo;{theme.name}&rdquo;</DialogTitle>
          <DialogDescription className="mt-1">
            Pick from your Keyword Bank or add new keywords. Selections will be
            assigned to the &ldquo;{theme.name}&rdquo; theme and added to your
            Master List.
          </DialogDescription>
        </DialogHeader>

        {/* Tabs */}
        <div className="flex border-b px-6 mt-3 shrink-0">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={cn(
                "pb-2 px-1 mr-5 text-sm font-medium border-b-2 -mb-px transition-colors",
                tab === t.id
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">
          {tab === "ai" && (
            <AddKeywordSignalAiTab
              themeName={theme.name}
              isGenerating={isGenerating}
              aiSuggestions={aiSuggestions}
              keywordById={keywordById}
              selectedIds={selectedIds}
              onToggleId={toggleId}
              onAskAI={handleAskAI}
              onSwitchToPick={() => setTab("pick")}
            />
          )}

          {tab === "pick" && (
            <AddKeywordPickAddTab
              search={search}
              onSearchChange={setSearch}
              pickableKeywords={pickableKeywords}
              selectedIds={selectedIds}
              onToggleId={toggleId}
              trimmedSearch={trimmedSearch}
              canAddSearchAsNewKeyword={canAddSearchAsNewKeyword}
              alreadyInThemeMatch={alreadyInThemeMatch}
              onAddTypedNewKeyword={addTypedNewKeyword}
            />
          )}

          {tab === "bulk" && (
            <AddKeywordBulkEntryTab
              themeName={theme.name}
              bulkText={bulkText}
              bulkParsed={bulkParsed}
              onBulkTextChange={(text, parsed) => {
                setBulkText(text);
                setBulkParsed(parsed);
              }}
              onRemoveParsed={removePendingNewKeyword}
            />
          )}

          {tab === "upload" && (
            <AddKeywordFileUploadTab
              themeName={theme.name}
              fileInputRef={fileInputRef}
              onFileUpload={handleFileUpload}
              uploadedFileName={uploadedFileName}
              uploadParsed={uploadParsed}
              onRemoveParsed={removePendingNewKeyword}
            />
          )}
        </div>

        {/* Footer */}
        <div className="border-t px-6 py-4 shrink-0">
          {totalSelectedCount > 0 && (
            <div className="mb-3 flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-muted-foreground shrink-0">
                {totalSelectedCount} keyword{totalSelectedCount !== 1 ? "s" : ""}{" "}
                selected:
              </span>
              {selectedKeywords.map((kw) => (
                <SelectedKeywordChip key={kw.id} label={kw.search} onRemove={() => toggleId(kw.id)} />
              ))}
              {pendingNewKeywords.map((phrase) => (
                <SelectedKeywordChip
                  key={phrase}
                  label={phrase}
                  onRemove={() => removePendingNewKeyword(phrase)}
                />
              ))}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={totalSelectedCount === 0 || isResolvingNew}
              className="gap-1.5"
            >
              {isResolvingNew && <Loader2 className="h-4 w-4 animate-spin" />}
              {`Add to "${theme.name}"${totalSelectedCount > 0 ? ` (${totalSelectedCount})` : ""}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
